import React from "react";
import {enqueueSnackbar} from "notistack";
import RestService from "../../services/api/RestService";
import NotistackVariants from "../../components/snackbar/NotistackVariants";
import {Clock} from "../../components/Clock/Clock";
import Editor from "../../components/Editor/Editor";
import ValidationResults from "../../components/ValidationResults/ValidationResults";
import AssignmentButtons from "../../components/AssignmentButtons/AssignmentButtons";
import AlertDialog from "../../components/AlertDialog/AlertDialog";
import i18n from "../../i18n/i18n";
import {LoadingBox} from "../../components/LoadingBox/LoadingBox";
import {
    instanceOfCompilationError,
    instanceOfInternalValidatorError,
    instanceOfUnknownError
} from "../../data/ValidationResult";
import {Typography} from "@mui/material";
import { withStyles } from 'tss-react/mui';
import {withTranslation} from "react-i18next";
import withRouter from "../../utils/withRouter";

const styles = {
    root: {
        paddingBottom: "1px" // HACK: prevent margin collapsing
    },
    assignmentElement: {
        marginBottom: "30px"
    },
    withWhiteSpace: {
        whiteSpace: "pre-wrap"
    },
    loadingBox: {
        position: "absolute",
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)"
    }
};

class CodeAssignmentPage extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            testInfo: undefined,
            assignment: undefined,
            assignmentAnswer: "",
            validationResult: undefined,
            solutionIsBeingValidated: false,
            solutionIsBeingSent: false,
            showSendSolutionDialog: false,
            newAssigmentIsBeingLoaded: false
        };

        this.handleAssignmentAnswerChange = this.handleAssignmentAnswerChange.bind(this);
        this.handleAssignmentAnswerCheck = this.handleAssignmentAnswerCheck.bind(this);
        this.handleAssignmentAnswerSend = this.handleAssignmentAnswerSend.bind(this);
    }

    componentDidMount() {
        this.getAssignment();
    }

    getCurrentTime() {
        return new Date().toLocaleTimeString();
    }

    getAssignment() {
        const securityCode = this.props.router.params.securityCode
        RestService.getNextUnsolvedAssignment(securityCode)
            .then(res => {
                this.setState({
                    assignment: res,
                    assignmentAnswer: res.template,
                    validationResult: "",
                    newAssigmentIsBeingLoaded: false
                });
                this.getTestInfo();
            })
            .catch(error => {
                RestService.getTestInfo(securityCode).then(test => {
                    if (test.canBeSolved) {
                        this.props.router.navigate("/feedback/" + securityCode);
                    } else {
                        enqueueSnackbar(error.message, NotistackVariants.error);
                        this.props.router.navigate("/not_found");
                    }
                });
            });
    }

    getTestInfo() {
        RestService.getTestInfo(this.props.router.params.securityCode)
            .then(json => this.setState({testInfo: json}))
            .catch(error => enqueueSnackbar(error.message, NotistackVariants.error));
    }

    endCurrentAssignment() {
        const {testInfo} = this.state;
        const securityCode = this.props.router.params.securityCode

        let currentAssignmentNumber = testInfo.doneAssignmentsNumber;
        let testAssignmentsCount = testInfo.assignmentsNumber;
        if (currentAssignmentNumber >= testAssignmentsCount - 1) {
            this.props.router.navigate("/feedback/" + securityCode);
        } else {
            this.setState({newAssigmentIsBeingLoaded: true});
            this.getAssignment();
        }
    }

    handleAssignmentAnswerChange(assignmentAnswer) {
        this.setState({assignmentAnswer: assignmentAnswer});
    }

    handleAssignmentAnswerCheck() {
        const {solutionIsBeingValidated, assignment, assignmentAnswer} = this.state;
        const securityCode = this.props.router.params.securityCode

        if (solutionIsBeingValidated) {
            return;
        }

        this.setState({
            solutionIsBeingValidated: true,
            validationResult: null,
        });

        RestService.checkTask(securityCode, assignment.code, assignmentAnswer).then(solutionCheck => {
            const validationResult = solutionCheck.validationResult;

            if (solutionCheck.status === 200) {
                enqueueSnackbar(i18n.t("codeAssignmentPage:solutionValidated", {time: this.getCurrentTime()}), NotistackVariants.info);
                this.setState({validationResult: validationResult});
            } else {
                if (instanceOfCompilationError(validationResult)) {
                    enqueueSnackbar(i18n.t("codeAssignmentPage:solutionValidationError", {time: this.getCurrentTime()}), NotistackVariants.error);
                    this.setState({validationResult: validationResult});
                } else {
                    if (instanceOfInternalValidatorError(validationResult) || instanceOfUnknownError(validationResult)) {
                        enqueueSnackbar(validationResult.message, NotistackVariants.error);
                    } else {
                        enqueueSnackbar(i18n.t("codeAssignmentPage:solutionValidationError", {time: this.getCurrentTime()}), NotistackVariants.error);
                    }

                    this.setState({validationResult: ""});
                }
            }
            this.setState({solutionIsBeingValidated: false});
        });
    }

    handleAssignmentAnswerSend() {
        const {assignment, assignmentAnswer} = this.state;
        const securityCode = this.props.router.params.securityCode

        this.setState({solutionIsBeingSent: true});

        RestService.saveTask(securityCode, assignment.code, assignmentAnswer)
            .then(_ => {
                enqueueSnackbar(i18n.t("codeAssignmentPage:solutionSent"), NotistackVariants.success);
                this.setState({solutionIsBeingSent: false});
                this.endCurrentAssignment();
            })
            .catch(error => {
                this.setState({solutionIsBeingSent: false});
                enqueueSnackbar(error.message, NotistackVariants.error);
                if (error.status === 404) {
                    this.props.router.navigate("/not_found");
                }
            });
    }

    render() {
        const {classes} = this.props;

        const {
            assignment,
            testInfo,
            assignmentAnswer,
            solutionIsBeingSent,
            solutionIsBeingValidated,
            validationResult,
            showSendSolutionDialog,
            newAssigmentIsBeingLoaded
        } = this.state;

        if (!assignment) {
            return null;
        }

        const currentAssignmentNumber = testInfo ? testInfo.doneAssignmentsNumber + 1 : null;
        const assignmentsCount = testInfo ? testInfo.assignmentsNumber : null;

        return (
            <div className={classes.root}>
                {
                    newAssigmentIsBeingLoaded ?
                        <div className={classes.loadingBox}><LoadingBox message={i18n.t("codeAssignmentPage:loading")}/>
                        </div> :
                        <div>
                            <Typography variant="h3" gutterBottom={true}>
                                {i18n.t("codeAssignmentPage:taskOf", {
                                    number: currentAssignmentNumber,
                                    total: assignmentsCount,
                                    title: assignment.title
                                })}
                            </Typography>
                        <Clock
                            startTime={assignment.startTime}
                            now={assignment.now}
                            duration={assignment.duration}
                            onTimeElapsed={this.handleAssignmentAnswerSend}
                            solutionIsBeingSent={solutionIsBeingSent}
                        />
                        <div className={classes.assignmentElement}>
                            <Typography variant="body1" classes={{root: classes.withWhiteSpace}}>
                                {assignment.text}
                            </Typography>
                        </div>
                        <div className={classes.assignmentElement}>
                            <Editor value={assignmentAnswer}
                                    onValueChange={this.handleAssignmentAnswerChange}/>
                        </div>
                        <div className={classes.assignmentElement}>
                            <AssignmentButtons
                                disabled={assignmentAnswer.length === 0}
                                onAssignmentTest={this.handleAssignmentAnswerCheck}
                                onAssignmentSend={() => this.setState({showSendSolutionDialog: true})}
                                solutionIsBeingValidated={solutionIsBeingValidated}
                                solutionIsBeingSent={solutionIsBeingSent}
                            />
                        </div>
                            <div className={classes.assignmentElement} data-cy="validationResults">
                                <ValidationResults log={validationResult}/>
                            </div>
                    </div>
            }

                <AlertDialog open={showSendSolutionDialog}
                             dialogTitle={i18n.t("codeAssignmentPage:sendConfirmationDialog.title")}
                             dialogContent={i18n.t("codeAssignmentPage:sendConfirmationDialog.content")}
                             firstButtonText={i18n.t("codeAssignmentPage:sendConfirmationDialog.confirm")}
                             secondButtonText={i18n.t("codeAssignmentPage:sendConfirmationDialog.cancel")}
                             firstButtonClick={() => {
                                 this.handleAssignmentAnswerSend();
                                 this.setState({showSendSolutionDialog: false});
                             }}
                             secondButtonClick={() => this.setState({showSendSolutionDialog: false})}
                             onClose={() => this.setState({showSendSolutionDialog: false})}/>
            </div>
        );
    }
}

const CodeAssignmentPageWithStyles = withStyles(CodeAssignmentPage, styles);
const CodeAssignmentPageWithRouter = withRouter(CodeAssignmentPageWithStyles)
export default withTranslation()(CodeAssignmentPageWithRouter);
